/**
 * File Upload Dialog Component
 *
 * 根据设计图重构的上传文件UI
 * - 显示待上传文件列表
 * - 检测重复文件（文件名+大小）
 * - 显示上传进度
 * - 阻止重复文件上传
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, FileText, AlertCircle, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fileUploadService } from '../../services/fileUploadService';

interface PendingFile {
  id: string;
  file: File;
  isDuplicate: boolean;
}

interface FileUploadDialogProps {
  knowledgeBaseId: string;
  existingFiles: Array<{ name: string; size: number }>;
  open: boolean;
  onClose: () => void;
  // 单个文件上传完成（乐观更新）
  onUploadSingleComplete: (file: File) => void;
  // 批量上传完成
  onUploadBatchComplete: (results: {
    success: string[];
    failed: Array<{ fileName: string; message: string }>;
  }) => void;
  onError: (error: { fileName: string; message: string }) => void;
}

export const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
  knowledgeBaseId,
  existingFiles,
  open,
  onClose,
  onUploadSingleComplete,
  onUploadBatchComplete,
  onError,
}) => {
  const { t } = useTranslation();
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
    percentage: number;
    isUploading: boolean;
  }>({
    current: 0,
    total: 0,
    percentage: 0,
    isUploading: false,
  });
  // 两个input ref：一个用于初始选择，一个用于添加更多文件
  const initialInputRef = useRef<HTMLInputElement>(null);
  const addMoreInputRef = useRef<HTMLInputElement>(null);

  // 构建已上传文件的key集合 (文件名|大小)
  const existingFileKeys = useCallback(() => {
    return new Set(existingFiles.map(f => `${f.name}|${f.size}`));
  }, [existingFiles]);

  // 检查文件是否重复
  const checkDuplicate = useCallback(
    (file: File): boolean => {
      const keys = existingFileKeys();
      return keys.has(`${file.name}|${file.size}`);
    },
    [existingFileKeys]
  );

  // 添加文件到待上传列表
  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const newPendingFiles: PendingFile[] = fileArray.map(file => ({
        id: `pending-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        isDuplicate: checkDuplicate(file),
      }));
      setPendingFiles(prev => [...prev, ...newPendingFiles]);
    },
    [checkDuplicate]
  );

  // 删除待上传文件
  const removeFile = useCallback((id: string) => {
    setPendingFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // 处理文件选择
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      addFiles(files);
      // 清空input以允许重复选择同一文件
      e.target.value = '';
    }
  };

  // 触发初始文件选择
  const triggerInitialFileSelect = () => {
    initialInputRef.current?.click();
  };

  // 触发添加更多文件选择
  const triggerAddMoreFileSelect = () => {
    addMoreInputRef.current?.click();
  };

  // 处理拖拽上传
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      addFiles(files);
    }
  };

  // 开始上传
  const handleUpload = async () => {
    const filesToUpload = pendingFiles.filter(f => !f.isDuplicate);
    if (filesToUpload.length === 0) return;

    setUploadProgress({
      current: 0,
      total: filesToUpload.length,
      percentage: 0,
      isUploading: true,
    });

    const successFiles: string[] = [];
    const failedFiles: Array<{ fileName: string; message: string }> = [];
    let completedCount = 0;
    const totalFiles = filesToUpload.length;

    // 调用上传服务
    await fileUploadService.uploadFilesToKnowledgeBase(
      filesToUpload.map(f => f.file),
      knowledgeBaseId,
      {
        t,
        onProgress: progress => {
          if (progress.show) {
            setUploadProgress({
              current: progress.current,
              total: progress.total,
              percentage: Math.round((progress.current / progress.total) * 100),
              isUploading: true,
            });
          }
        },
        onComplete: (fileName, file) => {
          completedCount++;
          successFiles.push(fileName);

          // 乐观更新：立即在UI中显示文件
          if (file) {
            onUploadSingleComplete(file);
          }

          // 所有文件都完成时才关闭对话框
          if (completedCount >= totalFiles) {
            setUploadProgress(prev => ({ ...prev, isUploading: false, percentage: 100 }));

            // 延迟关闭，让用户看到100%的进度
            setTimeout(() => {
              // 调用批量完成回调
              onUploadBatchComplete({
                success: successFiles,
                failed: failedFiles,
              });
              setPendingFiles([]);
              onClose();
            }, 500);
          }
        },
        onError: (fileName, message) => {
          completedCount++;
          failedFiles.push({ fileName, message });
          onError({ fileName, message });

          // 即使出错也要计数，所有文件处理完后关闭
          if (completedCount >= totalFiles) {
            setUploadProgress(prev => ({ ...prev, isUploading: false }));

            setTimeout(() => {
              // 调用批量完成回调
              onUploadBatchComplete({
                success: successFiles,
                failed: failedFiles,
              });
              setPendingFiles([]);
              onClose();
            }, 500);
          }
        },
      }
    );
  };

  // 重置状态
  const reset = useCallback(() => {
    setPendingFiles([]);
    setUploadProgress({
      current: 0,
      total: 0,
      percentage: 0,
      isUploading: false,
    });
  }, []);

  // 当对话框关闭时重置状态
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  // 当existingFiles变化时，重新检查重复
  useEffect(() => {
    setPendingFiles(prev =>
      prev.map(f => ({
        ...f,
        isDuplicate: checkDuplicate(f.file),
      }))
    );
  }, [existingFiles, checkDuplicate]);

  if (!open) return null;

  const hasDuplicateFiles = pendingFiles.some(f => f.isDuplicate);
  const canUpload = pendingFiles.some(f => !f.isDuplicate) && !uploadProgress.isUploading;
  const duplicateCount = pendingFiles.filter(f => f.isDuplicate).length;
  const validCount = pendingFiles.filter(f => !f.isDuplicate).length;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
      <div className='bg-white rounded-2xl shadow-lg w-full max-w-md'>
        {/* Header */}
        <div className='p-6 border-b border-gray-200'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center'>
                <Upload className='w-6 h-6 text-white' />
              </div>
              <div>
                <h2 className='text-lg font-semibold text-gray-900'>
                  {t('knowledge.files.upload.title')}
                </h2>
                <p className='text-sm text-gray-500'>{t('knowledge.files.upload.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={uploadProgress.isUploading}
              className='text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className='p-6'>
          {/* 上传区域 */}
          {pendingFiles.length === 0 && (
            <div
              className='border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors'
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={triggerInitialFileSelect}
            >
              <input
                ref={initialInputRef}
                type='file'
                multiple
                className='hidden'
                onChange={handleFileSelect}
                accept='.pdf,.doc,.docx,.txt,.md,.xlsx,.xls,.csv,.json,.xml,.png,.jpg,.jpeg,.gif'
              />
              <Upload className='w-12 h-12 mx-auto text-gray-400 mb-4' />
              <p className='text-sm font-medium text-gray-900 mb-1'>
                {t('knowledge.files.upload.dragAndDrop')}
              </p>
              <p className='text-xs text-gray-500 mb-4'>
                {t('knowledge.files.upload.supportedFormats')}
              </p>
              <button className='px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600'>
                {t('knowledge.files.upload.selectFiles')}
              </button>
            </div>
          )}

          {/* 文件列表 */}
          {pendingFiles.length > 0 && (
            <div className='space-y-3 mb-4'>
              {pendingFiles.map(pendingFile => (
                <div
                  key={pendingFile.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    pendingFile.isDuplicate
                      ? 'bg-red-50 border-red-200'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className='flex items-center gap-3 flex-1 min-w-0'>
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        pendingFile.isDuplicate ? 'bg-red-100' : 'bg-green-100'
                      }`}
                    >
                      {pendingFile.isDuplicate ? (
                        <AlertCircle className='w-4 h-4 text-red-600' />
                      ) : (
                        <FileText className='w-4 h-4 text-green-600' />
                      )}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div
                        className={`text-sm font-medium truncate ${
                          pendingFile.isDuplicate ? 'text-red-900' : 'text-gray-900'
                        }`}
                      >
                        {pendingFile.file.name}
                      </div>
                      <div
                        className={`text-xs ${
                          pendingFile.isDuplicate ? 'text-red-600' : 'text-gray-500'
                        }`}
                      >
                        {formatFileSize(pendingFile.file.size)}
                        {pendingFile.isDuplicate &&
                          ` · ${t('knowledge.files.upload.duplicateExists')}`}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(pendingFile.id)}
                    disabled={uploadProgress.isUploading}
                    className='text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed p-1'
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 重复文件警告 */}
          {hasDuplicateFiles && !uploadProgress.isUploading && (
            <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2'>
              <AlertCircle className='w-4 h-4 text-red-600 flex-shrink-0 mt-0.5' />
              <div className='flex-1'>
                <p className='text-sm text-red-900 font-medium'>
                  {t('knowledge.files.upload.duplicateCount', { count: duplicateCount })}
                </p>
                <p className='text-xs text-red-700 mt-1'>
                  {t('knowledge.files.upload.deleteDuplicateWarning')}
                </p>
              </div>
            </div>
          )}

          {/* 进度区域 */}
          {uploadProgress.isUploading && (
            <div className='p-4 bg-gray-50 rounded-lg'>
              <div className='flex items-center justify-between mb-2'>
                <p className='text-sm text-gray-700'>{t('knowledge.files.upload.progress')}</p>
                <p className='text-sm font-medium text-blue-600'>{uploadProgress.percentage}%</p>
              </div>
              <div className='w-full bg-gray-200 rounded-full h-1 mb-2'>
                <div
                  className='bg-blue-500 h-1 rounded-full transition-all duration-300'
                  style={{ width: uploadProgress.percentage + '%' }}
                />
              </div>
              <div className='flex items-center gap-2'>
                <Info className='w-4 h-4 text-blue-500 flex-shrink-0' />
                <p className='text-xs text-gray-500'>
                  {t('knowledge.files.upload.processingNote')}
                </p>
              </div>
            </div>
          )}

          {/* 添加更多文件按钮 */}
          {pendingFiles.length > 0 && !uploadProgress.isUploading && (
            <>
              <input
                ref={addMoreInputRef}
                type='file'
                multiple
                className='hidden'
                onChange={handleFileSelect}
                accept='.pdf,.doc,.docx,.txt,.md,.xlsx,.xls,.csv,.json,.xml,.png,.jpg,.jpeg,.gif'
              />
              <button
                onClick={triggerAddMoreFileSelect}
                className='w-full mt-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors'
              >
                {t('knowledge.files.upload.addMore')}
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className='p-6 border-t border-gray-200 flex justify-end gap-3'>
          <button
            onClick={onClose}
            disabled={uploadProgress.isUploading}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {t('knowledge.files.upload.cancel')}
          </button>
          <button
            onClick={handleUpload}
            disabled={!canUpload}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
              canUpload
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {uploadProgress.isUploading ? (
              <>
                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                {t('knowledge.files.upload.uploading')}
              </>
            ) : (
              <>
                {validCount > 0
                  ? t('knowledge.files.upload.uploadXFiles', { count: validCount })
                  : t('knowledge.files.upload.upload')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUploadDialog;
